import { FC } from 'react'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import {
  ReviewComparisonPlatformCard,
  ReviewComparisonPlatforms,
} from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './platformPerformance.module.scss'

interface PlatformPerformanceProps {
  facilities: ReviewComparisonPlatforms[]
}

const COPY = {
  best: 'Best Platform',
  // A facility can be in the comparison and have collected nothing rateable
  // in the window. Saying so is the difference between an empty column and a
  // broken one.
  empty: 'No platform reviews in this period.',
  rating: 'Rating',
  reviews: 'reviews',
  sentiment: 'Sentiment',
  subtitle: 'Where each facility earns its best and worst reviews',
  title: 'Platform Performance',
  worst: 'Worst Platform',
}

// The platform ids the API returns are slugs; these are what a reader calls
// them.
const PLATFORM_NAMES: Record<string, string> = {
  appstore: 'App Store',
  booking: 'Booking',
  expedia: 'Expedia',
  google: 'Google Maps',
  playstore: 'Google Play',
  tripadvisor: 'Tripadvisor',
}

// Ratings are out of five, which is what the bar under one is a share of.
const MAX_RATING = 5

const card = (entry: ReviewComparisonPlatformCard) => {
  const Icon = getPlatformIcon(entry.platform as ReviewPlatform)
  const tone = entry.isBest ? styles.good : styles.bad

  return (
    <article className={styles.card} key={entry.platform}>
      <div className={styles.cardHeader}>
        <Icon className={styles.logo} />

        <div className={styles.cardTitle}>
          <span className={styles.platformName}>
            {PLATFORM_NAMES[entry.platform] ?? entry.platform} —{' '}
            {entry.isBest ? COPY.best : COPY.worst}
          </span>
          <span className={styles.reviews}>
            {entry.reviews.toLocaleString('en-US')} {COPY.reviews}
          </span>
        </div>

        <span className={cn(styles.rank, tone)}>{entry.rank}</span>
      </div>

      <span className={styles.divider} />

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{COPY.rating}</span>
          <span className={styles.statValue}>
            {entry.rating.toFixed(1).replace('.', ',')}
            <span className={styles.star}>★</span>
          </span>
          <span className={styles.track}>
            <span
              className={cn(styles.fill, tone)}
              style={{ width: `${(entry.rating / MAX_RATING) * 100}%` }}
            />
          </span>
        </div>

        <div className={styles.stat}>
          <span className={styles.statLabel}>{COPY.sentiment}</span>
          <span className={styles.statValue}>
            {entry.sentiment}%
            <span className={styles.statCaption}>{entry.sentimentLabel}</span>
          </span>
          <span className={styles.track}>
            <span
              className={cn(styles.fill, tone)}
              style={{ width: `${entry.sentiment}%` }}
            />
          </span>
        </div>
      </div>
    </article>
  )
}

// One column per facility, each holding the platform it does best on and the
// one it does worst on — the point being the gap between them, which is why
// both columns are read at once rather than one facility at a time.
export const PlatformPerformance: FC<PlatformPerformanceProps> = ({
  facilities,
}) => (
  <section className={styles.section}>
    <div className={styles.headerText}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.columns}>
      {facilities.map((entry) => (
        <div className={styles.column} key={entry.facilityId}>
          <h4 className={styles.facility}>
            <TruncatedTooltip text={entry.facilityName} />
          </h4>
          {entry.cards.length ? (
            entry.cards.map(card)
          ) : (
            <p className={styles.empty}>{COPY.empty}</p>
          )}
        </div>
      ))}
    </div>
  </section>
)
